import { Button, Collapse } from "reactstrap";
import { Dispatch, useState } from "react";
import { CheckPicker } from "rsuite";

import { Field, Module } from "../../types/TenantInterfaces";
import ManageItems from "../ManageItems/ManageItems";
import AppTooltip from "../../elements/AppTooltip";
import { DrawerError } from "../Drawers/AppDrawer";
import useFieldMap from "../../hooks/useFieldMap";

interface ModuleFieldsPickerI {
  module: Module;
  availableFields: Field[];
  values: string[];
  fieldSchema?: Field;
  layoutLimit?: number;
  setError: Dispatch<DrawerError | null>;
  setValues: (newValues: string[]) => void;
  onChange: (newFields: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

interface FieldtypesButtonI {
  name: string;
  color: string;
  description: string;
  onClick: () => void;
}

const FieldtypesButton = ({
  name,
  color,
  description,
  onClick,
}: FieldtypesButtonI) => {
  return (
    <AppTooltip content={description} useTooltip={true}>
      <Button
        className="text-white"
        size="sm"
        style={{ backgroundColor: color }}
        onClick={onClick}
      >
        {name}
      </Button>
    </AppTooltip>
  );
};

const ModuleFieldsPicker = ({
  module,
  values,
  setValues,
  fieldSchema,
  layoutLimit,
  availableFields,
  setError,
  onChange,
  placeholder = "Select Layout Fields",
  disabled = false,
}: ModuleFieldsPickerI) => {
  const [showQuickOptions, setShowQuickOptions] = useState(false);
  const [sorted, setSorted] = useState(false);
  const fieldMap = useFieldMap(
    module?.APIName,
    "",
    false,
    fieldSchema?.APIName
  );
  const matchedFields = values.flatMap((APIName) => {
    return fieldMap[APIName] ? [fieldMap[APIName]] : [];
  });

  return (
    <>
      <CheckPicker
        preventOverflow
        data={availableFields}
        value={values}
        block={true}
        labelKey={"Name"}
        valueKey={"APIName"}
        placeholder={placeholder}
        onChange={(values) => {
          if (layoutLimit && values.length > layoutLimit) {
            setError({
              message: `You can only add ${layoutLimit} fields.`,
            });
            return;
          }

          setError(null);
          onChange(values);
        }}
        disabled={disabled}
        searchable={true}
      />

      <div className="font-weight-500 text-primary small cursor-pointer d-flex align-items-center">
        <i
          className={
            showQuickOptions ? "mdi mdi-chevron-down" : "mdi mdi-chevron-right"
          }
          style={{ fontSize: 18 }}
          onClick={() => setShowQuickOptions((prev) => !prev)}
        />
        <div
          className="flex-grow-1"
          onClick={() => setShowQuickOptions((prev) => !prev)}
        >
          Show Quick Options
        </div>
        {values?.length > 1 && (
          <div onClick={handleSort}>Sort Fields {sorted ? "Z-A" : "A-Z"}</div>
        )}
      </div>
      <div className="mb-2">
        <Collapse isOpen={showQuickOptions}>
          <div className="d-flex flex-wrap mt-1" style={{ gap: 10 }}>
            <FieldtypesButton
              name="Text Fields"
              description={"Add Text, Long Text, and Rich Text fields."}
              color={"var(--primary)"}
              onClick={addByFieldType(["Text", "LongText", "RichText"])}
            />
            <FieldtypesButton
              name="Phone, Email, URL"
              description={"Add Phone, Email, and URL fields."}
              color={"var(--secondary)"}
              onClick={addByFieldType(["Phone", "Email", "URL"])}
            />
            <FieldtypesButton
              name="Numbers"
              description={
                "Add Number, Percentage, Currency and CurrencyInterval fields."
              }
              color={"#16376b"}
              onClick={addByFieldType([
                "Number",
                "Percent",
                "Currency",
                "CurrencyInterval",
              ])}
            />
            <FieldtypesButton
              name="References"
              description={"Add Single Reference, and Multi Reference fields."}
              color={"#ff7700"}
              onClick={addByFieldType(["SingleReference", "MultiReference"])}
            />
            <FieldtypesButton
              name="Selects"
              description={"Add Single Select, and Multi Select fields."}
              color={"#861cff"}
              onClick={addByFieldType(["SingleSelect", "MultiSelect"])}
            />
            <FieldtypesButton
              name="Date/Time"
              description={"Add Date, Time, and Date Time fields."}
              color={"#007bb0"}
              onClick={addByFieldType(["Date", "Time", "DateTime"])}
            />
            <FieldtypesButton
              name="File Related"
              description={"Add Attachments, and Image fields."}
              color={"#4f4f4f"}
              onClick={addByFieldType(["SingleFile", "Image"])}
            />
            <FieldtypesButton
              name="Location"
              description={"Add Location fields."}
              color={"#8f004a"}
              onClick={addByFieldType(["Location"])}
            />
            <FieldtypesButton
              name="Toggle"
              description={"Add Toggle fields."}
              color={"#00754c"}
              onClick={addByFieldType(["Toggle"])}
            />
            <FieldtypesButton
              name="Subitems"
              description={"Add Subitem, and Multi Subitem fields."}
              color={"#4f004f"}
              onClick={addByFieldType(["Subitem", "MultiSubitem"])}
            />
          </div>
        </Collapse>
      </div>
      <ManageItems
        idKey="APIName"
        itemKey={undefined}
        items={matchedFields}
        onDelete={handleRemoveField}
        onReorder={handleReorderFields}
        description="No fields have been added."
      />
    </>
  );

  function handleSort() {
    const newFields = sorted ? values?.reverse() : values.sort();
    setValues(newFields);
    setSorted((prev) => !prev);
  }

  function handleRemoveField({ id }: { id: string }) {
    const updatedFields = values.filter((f) => f !== id);
    setValues(updatedFields);
  }

  function handleReorderFields({ newOrder }: { newOrder: string[] }) {
    setSorted(false);
    setValues(newOrder);
  }

  function addByFieldType(fieldTypes: string[]) {
    return () => {
      const fieldsToAdd: string[] = [];
      let fieldsToCheck: Field[] = [];
      const availableFieldAPINames = availableFields.map((f) => f.APIName);

      if (fieldSchema) {
        fieldsToCheck = fieldSchema?.SubitemFields ?? [];
      } else {
        fieldsToCheck = availableFields;
      }

      for (const field of fieldsToCheck) {
        const type = field.FieldType;
        const apiName = field.APIName;

        if (
          ["id", "_RecordName"].includes(apiName) ||
          !availableFieldAPINames.includes(apiName)
        )
          continue;

        if (fieldTypes.includes(type) && !values.includes(apiName)) {
          if (layoutLimit && fieldsToAdd.length >= layoutLimit) continue;
          fieldsToAdd.push(apiName);
        }
      }

      if (fieldsToAdd.length > 0) {
        setSorted(false);

        if (typeof layoutLimit !== "number") {
          return setValues([...values, ...fieldsToAdd]);
        }

        const currentLength = values?.length;
        const fieldsToAddLength = fieldsToAdd.length;

        if (currentLength === layoutLimit) {
          return setError({
            message: `You can only add ${layoutLimit} fields.`,
          });
        }

        const remainingSpace = currentLength + fieldsToAddLength;

        if (remainingSpace > layoutLimit) {
          setError({
            message: `You can only add ${layoutLimit} fields.`,
          });

          const prevCopy = [...values];

          for (let i = 0; prevCopy.length < layoutLimit; i++) {
            prevCopy.push(fieldsToAdd[i]);
          }

          setValues(prevCopy);
          return;
        }

        setValues([...values, ...fieldsToAdd]);
      }
    };
  }
};
export default ModuleFieldsPicker;
